// Đọc hướng la bàn từ cảm biến điện thoại.
//
// iOS 13+ yêu cầu gọi DeviceOrientationEvent.requestPermission() từ một thao tác
// người dùng, và trả sẵn `webkitCompassHeading` (0° = Bắc, thuận chiều kim đồng
// hồ). Android dùng sự kiện `deviceorientationabsolute` với `alpha` quay ngược
// chiều kim đồng hồ nên phải lấy 360 − alpha, đồng thời bù góc xoay màn hình.
import { useCallback, useEffect, useRef, useState } from "react";

type TrangThai =
  | "chua-bat"
  | "dang-cho"
  | "dang-chay"
  | "tu-choi"
  | "khong-ho-tro"
  | "khong-an-toan" // trang không chạy HTTPS → trình duyệt chặn cảm biến
  | "khong-tin-hieu"; // đã đăng ký nghe nhưng máy không gửi dữ liệu hướng

/** Chờ bao lâu mà không có dữ liệu hướng thì coi như máy không có từ kế. */
const HAN_CHO_TIN_HIEU_MS = 3000;

type SuKienHuong = DeviceOrientationEvent & { webkitCompassHeading?: number };

type CoRequestPermission = {
  requestPermission?: () => Promise<"granted" | "denied" | "default">;
};

/** Trung bình trượt theo vòng tròn để kim đỡ rung, vẫn bám kịp khi xoay nhanh. */
function lamMuot(truoc: number | null, moi: number, heSo = 0.25): number {
  if (truoc === null) return moi;
  let lech = moi - truoc;
  while (lech > 180) lech -= 360;
  while (lech < -180) lech += 360;
  return (((truoc + lech * heSo) % 360) + 360) % 360;
}

export function useCompassHeading() {
  const [heading, setHeading] = useState<number | null>(null);
  const [trangThai, setTrangThai] = useState<TrangThai>("chua-bat");
  const mucTieu = useRef<number | null>(null);
  const dangNghe = useRef(false);
  const dongHoCho = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Phải dò sau khi mount, KHÔNG tính thẳng trong lượt render: lượt render đầu
  // ở client là hydrate lại HTML do server dựng (server không có `window` nên
  // luôn ra false), mà React không sửa lệch thuộc tính khi hydrate — nút sẽ kẹt
  // ở trạng thái disabled vĩnh viễn.
  const [hoTro, setHoTro] = useState(false);

  useEffect(() => {
    const co = "ondeviceorientationabsolute" in window || "ondeviceorientation" in window;
    setHoTro(co);
    if (!co) setTrangThai("khong-ho-tro");
    else if (!window.isSecureContext) setTrangThai("khong-an-toan");
  }, []);

  const xuLy = useCallback((e: Event) => {
    const ev = e as SuKienHuong;
    let goc: number | null = null;

    if (typeof ev.webkitCompassHeading === "number" && !Number.isNaN(ev.webkitCompassHeading)) {
      goc = ev.webkitCompassHeading;
    } else if (
      typeof ev.alpha === "number" &&
      (ev.absolute || e.type === "deviceorientationabsolute")
    ) {
      const gocManHinh = window.screen?.orientation?.angle ?? 0;
      goc = 360 - ev.alpha + gocManHinh;
    }

    if (goc === null) return;
    if (dongHoCho.current) {
      clearTimeout(dongHoCho.current);
      dongHoCho.current = null;
    }
    const chuanHoa = ((goc % 360) + 360) % 360;
    mucTieu.current = lamMuot(mucTieu.current, chuanHoa);
    setHeading(mucTieu.current);
    setTrangThai("dang-chay");
  }, []);

  const nghe = useCallback(() => {
    if (dangNghe.current) return;
    dangNghe.current = true;
    window.addEventListener("deviceorientationabsolute", xuLy, true);
    window.addEventListener("deviceorientation", xuLy, true);
  }, [xuLy]);

  const batLaBan = useCallback(async () => {
    if (!hoTro) {
      setTrangThai("khong-ho-tro");
      return;
    }
    if (!window.isSecureContext) {
      setTrangThai("khong-an-toan");
      return;
    }
    const ctor = window.DeviceOrientationEvent as unknown as CoRequestPermission | undefined;
    if (typeof ctor?.requestPermission === "function") {
      setTrangThai("dang-cho");
      try {
        const ket = await ctor.requestPermission();
        if (ket !== "granted") {
          setTrangThai("tu-choi");
          return;
        }
      } catch {
        setTrangThai("tu-choi");
        return;
      }
    }
    setTrangThai("dang-cho");
    nghe();
    // Đăng ký nghe thành công không có nghĩa là máy có từ kế — nhiều máy tính
    // và một số điện thoại không bao giờ bắn sự kiện. Hết hạn chờ thì báo rõ.
    if (dongHoCho.current) clearTimeout(dongHoCho.current);
    dongHoCho.current = setTimeout(() => {
      setTrangThai((t) => (t === "dang-chay" ? t : "khong-tin-hieu"));
    }, HAN_CHO_TIN_HIEU_MS);
  }, [hoTro, nghe]);

  const tatLaBan = useCallback(() => {
    window.removeEventListener("deviceorientationabsolute", xuLy, true);
    window.removeEventListener("deviceorientation", xuLy, true);
    if (dongHoCho.current) {
      clearTimeout(dongHoCho.current);
      dongHoCho.current = null;
    }
    dangNghe.current = false;
    mucTieu.current = null;
    setHeading(null);
    setTrangThai("chua-bat");
  }, [xuLy]);

  useEffect(() => {
    return () => {
      window.removeEventListener("deviceorientationabsolute", xuLy, true);
      window.removeEventListener("deviceorientation", xuLy, true);
      if (dongHoCho.current) clearTimeout(dongHoCho.current);
      dangNghe.current = false;
    };
  }, [xuLy]);

  return { heading, trangThai, hoTro, batLaBan, tatLaBan };
}
